<template>
  <div class="container">
    <PreviousNavLink :go-to-link="'/organisation/' + orgId"
                     :previous-label="`Tillbaka till ${organisation.displayName}`"></PreviousNavLink>
    <h1 class="page-header">
      {{ state.editMode ? `${state.form.firstName} ${state.form.lastName}` : "Lägg till organisationsadministratör"
      }}</h1>
    <p class="mb-4">Här kan du lägga till eller ta bort organisationsadministratörer. Om du vill att de ska kunna
      hantera Digitala samlingen kryssar du i den rutan. </p>
    <form @submit.prevent="onSubmit">
      <div class="row form-container">
        <div class="col-lg-6">
          <h3 class="my-4">Personuppgifter</h3>
          <div class="form-group">
            <label class="form-check-label&#45;&#45;normal" for="firstName">Förnamn</label>
            <input id="firstName" v-model="state.form.firstName" :readonly="state.editMode" aria-required="true"
                   class="form-control"
                   name="firstName" placeholder="Förnamn"
                   required type="text">
          </div>
          <div class="form-group">
            <label class="form-check-label&#45;&#45;normal" for="lastName">Efternamn</label>
            <input id="lastName" v-model="state.form.lastName" :readonly="state.editMode" class="form-control"
                   name="lastName"
                   placeholder="Efternamn" required
                   type="text">
          </div>
          <div class="form-group">
            <label class="form-check-label&#45;&#45;normal" for="email">E-post</label>
            <input id="email" v-model="state.form.email" :readonly="state.editMode" class="form-control" name="email"
                   placeholder="E-post" required
                   type="email">
          </div>
        </div>
        <div class="col-lg-6">
          <h3 class="my-4">Rättigheter</h3>
          <div class="form-check">
            <!--            <input id="isOrgRegAdmin" v-model="state.form.isOrgRegAdmin" class="form-check-input sr-only"
                               type="checkbox" />-->
            <span class="fas fa-check check"></span>
            <label class="form-check-label"><b>Organisationsadministratör</b></label>
            <p>Möjlighet att skapa och ta bort organisationsadministratörer i organisationsregistret.</p>
          </div>
          <div class="form-check">
            <input id="isDSResponsible" v-model="state.form.isDSResponsible" class="form-check-input sr-only"
                   type="checkbox" />
            <label class="form-check-label" for="isDSResponsible"><b>Digitala samlingen</b></label>
            <p>Möjlighet att köpa organisationslicens till Digitala samlingen i Webbutiken samt möjlighet att skapa
              skolor och lägga till användare i Digitala samlingen.</p>
          </div>
        </div>
      </div>
      <CtaPanel class="mt-5">
        <div class="buttons-container">
          <input v-if="state.editMode && !state.form.isHeadContact" :disabled="state.form.status === 'Invited'"
                 class="btn btn-danger" type="button" value="Ta bort" @click="showDeleteUserModal()" />
          <div class="buttons-container push-buttons-right">
            <button v-if="!state.editMode" class="cancel-button" type="button"
                    @click="showCancelModal()"><span class="fas fa-chevron-left mr-2"></span>Avbryt
            </button>
            <PreviousNavLink v-else :go-to-link="'/organisation/' + orgId"
                             :previous-label="`Tillbaka till ${organisation.displayName}`"></PreviousNavLink>
            <input :disabled="notValidForm" :value="state.editMode ? 'Spara' : 'Lägg till +'" class="btn btn-light mb-0"
                   type="submit">
          </div>
        </div>
      </CtaPanel>
      <Modal
        :isActive="state.showDeleteUserModal"
        buttonText="Ta bort"
        message="Vill du ta bort användaren?"
        size="xsmall"
        title="Ta bort användare"
        @cancel="state.showDeleteUserModal = false"
        @confirm="deleteUser"
        @update:is-active="state.showDeleteUserModal = $event"></Modal>

      <Modal
        :isActive="state.showCancelModal"
        buttonText="Ja"
        message="Organisationsadministratören har ej skapats än, vill du verkligen avsluta processen?"
        size="xsmall"
        title="Lägg till organisationsadmin"
        @cancel="state.showCancelModal = false"
        @confirm="goToOrganisation"
        @update:is-active="state.showCancelModal = $event"></Modal>

      <p v-if="state.form.status === 'Invited'" class="alert-info alert-container">En användare med status
        <i>Inbjuden</i> kan ej tas bort</p>
    </form>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive } from "@vue/runtime-core";
import { useRoute } from "vue-router/dist/vue-router";
import { CreateOrgUserRequest, useOrganisationUsersStore } from "@/stores/organisationUsers";
import CtaPanel from "../components/CtaPanel.vue";
import { UserRoleModel } from "@Shared/Models/UserRoleModel";
import { useRouter } from "vue-router";
import { useToastStore } from "@/stores/toast";
import { storeToRefs } from "pinia";
import { AxiosError } from "axios";
import Modal from "../components/Modal.vue";
import PreviousNavLink from "@/components/PreviousNavLink.vue";
import { useOrganisationStore } from "@/stores/organisation";
import { computed } from "vue";
import { OrganisationModel } from "@Shared/Models/OrganisationModel";

export default defineComponent({
  name: "OrgUserAdmin",
  components: {
    PreviousNavLink,
    CtaPanel,
    Modal
  },
  computed: {
    notValidForm() {
      return !(this.state.form.firstName?.length && this.state.form.lastName?.length && this.state.form.email?.length);
    }
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const { orgId, userId } = route.params;
    const organisationUsersStore = useOrganisationUsersStore();
    const toastStore = useToastStore();
    const organisationStore = useOrganisationStore();
    const { organisationUsers } = storeToRefs(organisationUsersStore);

    let currentUser: UserRoleModel = {
      organisationId: orgId as string,
      isOrganisationUser: false,
      isOrgRegAdmin: true,
      isDSResponsible: false,
      email: "",
      isHeadContact: false,
      lastName: "",
      firstName: "",
      id: "",
      status: ""
    };

    const state = reactive({
      orgId: "",
      editMode: false,
      form: <UserRoleModel>currentUser,
      showDeleteUserModal: false,
      showCancelModal: false
    });

    const setUser = (userId) => {
      const orgUser = organisationUsersStore.getOrganisationUser(userId as string) as UserRoleModel;

      if (orgUser) {
        state.editMode = true;
        state.form = orgUser;
      } else {
        state.form = currentUser;
      }
      return orgUser;
    };

    if (organisationUsersStore.organisationUsers?.length != 0) {
      setUser(userId);
    } else {
      organisationUsersStore.GetAllOrgUsers(orgId).then(() => {
        setUser(userId);
      });
    }

    const onSubmit = async () => {
      if (state.editMode) {
        const result = await organisationUsersStore.UpdateOrganisationUser(state.form);
        if (result) {
          toastStore.addToast({ message: "Organisationsadministratör uppdaterad", toastType: "success" });
          await router.push(`/organisation/${orgId}`);
        } else {
          toastStore.addToast({ message: "Organisationsadministratör kunde ej uppdateras", toastType: "danger" });
        }
      } else {
        const request: CreateOrgUserRequest = {
          organisationId: state.form.organisationId,
          firstName: state.form.firstName,
          lastName: state.form.lastName,
          email: state.form.email,
          isOrgRegAdmin: state.form.isOrgRegAdmin,
          isDSResponsible: state.form.isDSResponsible
        };
        const result = await organisationUsersStore.CreateOrganisationUser(request)
          .then((result) => {
            toastStore.addToast({
              message: "Inbjudan av organisationsadministratör har skickats",
              toastType: "success"
            });
            router.push(`/organisation/${orgId}`);
          })
          .catch((error) => {
            if ((error.message as AxiosError)?.response?.status === 409) {
              toastStore.addToast({
                message: "Det finns redan en medlem i organisationen eller en inbjudan med samma epost",
                toastType: "danger"
              });
            } else {
              toastStore.addToast({ message: "Organisationsadministratör kunde ej skapas", toastType: "danger" });
            }
          });
      }
    };

    const deleteUser = async () => {
      const result = await organisationUsersStore.DeleteOrganisationUser(state.form);
      if (result) {
        toastStore.addToast({ message: "Organisationsadministratör borttagen", toastType: "success" });
        await router.push(`/organisation/${orgId}`);
      } else {
        toastStore.addToast({ message: "Organisationsadministratör kunde ej tas bort", toastType: "danger" });
      }
    };

    const goToOrganisation = () => {
      router.push(`/organisation/${orgId}`);
    };

    const showDeleteUserModal = () => {
      state.showDeleteUserModal = true;
    };

    const showCancelModal = () => {
      state.showCancelModal = true;
    };

    return {
      state,
      orgId,
      organisation: computed(() => organisationStore.getOrganisationById(orgId as string) as OrganisationModel),
      onSubmit,
      deleteUser,
      showDeleteUserModal,
      showCancelModal,
      goToOrganisation
    };
  }
});
</script>

<style lang="scss" scoped>
form {
  padding: 1rem;

  .form-container {
    border-radius: 8px;
    opacity: 1;
    background-color: rgba(230, 238, 247, 0.5);
  }
}

.check {
  position: absolute;
  left: 0;
  bottom: 28px;
  color: #008511;
  font-size: smaller;
}

.cancel-button {
  color: rgba(53, 108, 154, 1);
  font-size: 16px;
  font-weight: 400;
  font-style: normal;
  text-decoration: underline;
  border: none;
  background-color: transparent;
}

.alert-container {
  padding: 1rem;
  width: max-content;
}
</style>